<!-- MonteCarloChart.vue -->
<template>
  <div class="chart-container">
    <div class="chart-header">
      <h3>Monte Carlo Simulation - Portfolio Projections</h3>
      <div class="chart-controls">
        <button 
          @click="runSimulation" 
          class="btn-simulate"
          :disabled="isSimulating"
        >
          {{ isSimulating ? 'Simulating...' : 'Run Simulation' }}
        </button>
        <div class="simulation-info" v-if="simulationData">
          <span class="info-item">{{ numSimulations }} simulations</span>
          <span class="info-item">Success Rate: {{ successRate }}%</span>
          <span class="info-item" v-if="simulationData.failureRate > 0" style="color: #e74c3c;">
            Failure Rate: {{ simulationData.failureRate }}%
          </span>
          <div class="setting-group">
            <label>Inflation Adjustment:</label>
            <select v-model="inflationOption" class="setting-input">
              <option value="none">None (Nominal)</option>
              <option value="fixed">Fixed Rate</option>
              <option value="random">Random (Normal Dist)</option>
            </select>
          </div>
          <div class="setting-group" v-if="inflationOption !== 'none'">
            <label>Inflation Rate:</label>
            <input 
              v-model.number="inflationRate" 
              type="number" 
              step="0.01" 
              min="0" 
              max="0.20"
              class="setting-input"
              placeholder="0.03"
            >
            </input>
          </div>
          <div class="setting-group" v-if="inflationOption === 'random'">
            <label>Inflation σ:</label>
            <input 
              v-model.number="inflationVolatility" 
              type="number" 
              step="0.005" 
              min="0" 
              max="0.10"
              class="setting-input"
              placeholder="0.01"
            >
            </input>
          </div>
        </div>
      </div>
      <div class="chart-wrapper">
        <HighChartsWrapper
          v-if="simulationData"
          name="monte-carlo"
          chart-type="area"
          :chart-options="highchartsOptions"
        />
        <div v-else class="empty-state">
          <p>Click "Run Simulation" to generate Monte Carlo projections</p>
        </div>
      </div>

      <div class="simulation-settings">
        <div class="setting-group">
          <label>Number of Simulations:</label>
          <select v-model.number="numSimulations" class="setting-input">
            <option :value="100">100 (Fast)</option>
            <option :value="500">500 (Balanced)</option>
            <option :value="1000">1,000 (Detailed)</option>
            <option :value="5000">5,000 (Comprehensive)</option>
          </select>
        </div>
        <div class="setting-group">
          <label>Return Volatility (σ):</label>
          <input 
            v-model.number="volatility" 
            type="number" 
            step="0.01" 
            min="0.05" 
            max="0.50"
            class="setting-input"
          >
          </input>
        </div>
        <div class="setting-group">
          <label>Success Threshold:</label>
          <input 
            v-model.number="successThreshold" 
            type="number" 
            step="10000"
            class="setting-input"
          >
          </input>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';

export default {
  name: 'MonteCarloChart',
  components: {
    HighChartsWrapper
  },
  props: {
    yearRows: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const numSimulations = ref(1000);
    const volatility = ref(0.15); // 15% standard deviation
    const successThreshold = ref(0); // Portfolio value above this at end
    const inflationOption = ref('random'); // 'none', 'fixed', 'random'
    const inflationRate = ref(0.03); // 3% default inflation
    const inflationVolatility = ref(0.01); // 1% inflation volatility
    const isSimulating = ref(false);
    const simulationData = ref(null);
    const successRate = ref(0);

    // Get liquid assets for portfolio simulation
    const liquidAssets = computed(() => {
      return props.items.filter(item => 
        item.type === 'asset' &&
        (item.category === 'Investments' || item.category === 'Cash & Bank')
      );
    });

    // Box-Muller transform for normal distribution
    const generateNormalRandom = (mean, stdDev) => {
      let u = 0, v = 0;
      while(u === 0) u = Math.random(); // Converting [0,1) to (0,1)
      while(v === 0) v = Math.random();
      const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
      return mean + z * stdDev;
    };

    // Get initial portfolio value and composition
    const getInitialPortfolioComposition = () => {
      const firstYear = props.yearRows[0];
      if (!firstYear) return { total: 0, weights: {} };
      
      let total = 0;
      const values = {};
      
      liquidAssets.value.forEach(asset => {
        const value = Math.abs(firstYear[`nw_${asset.id}`] || 0);
        values[asset.id] = value;
        total += value;
      });
      
      const weights = {};
      if (total > 0) {
        Object.keys(values).forEach(name => {
          weights[name] = values[name] / total;
        });
      }
      
      return { total, weights, values };
    };

    // Get base expected returns for each asset
    const getAssetReturns = () => {
      const returns = {};
      liquidAssets.value.forEach(asset => {
        returns[asset.id] = asset.growth_rate || 0.07; // Default 7% if not specified
      });
      return returns;
    };

    // Get target allocation for rebalancing (using initial weights as target)
    const getTargetAllocation = () => {
      const initial = getInitialPortfolioComposition();
      return initial.weights;
    };

    // Run Monte Carlo simulation
    const runSimulation = async () => {
      isSimulating.value = true;
      
      // Delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const years = props.yearRows.map(r => r.year);
      const initial = getInitialPortfolioComposition();
      const assetReturns = getAssetReturns();
      const targetAllocation = getTargetAllocation();
      const simResults = [];
      let failures = 0;
      
      // Run simulations
      for (let sim = 0; sim < numSimulations.value; sim++) {
        const path = [initial.total];
        let hasFailure = false;
        
        // Track individual asset values for this simulation
        let assetValues = { ...initial.values };
        let currentTotal = initial.total;
        let cumulativeInflation = 1; // Track cumulative inflation for this simulation
        
        for (let yearIndex = 1; yearIndex < years.length; yearIndex++) {
          const yearData = props.yearRows[yearIndex];
          
          // Calculate inflation for this year if enabled
          let yearlyInflation = 0;
          if (inflationOption.value === 'fixed') {
            yearlyInflation = inflationRate.value;
          } else if (inflationOption.value === 'random') {
            // Random inflation with normal distribution
            yearlyInflation = generateNormalRandom(inflationRate.value, inflationVolatility.value);
            yearlyInflation = Math.max(0, yearlyInflation); // Don't allow deflation
          }
          cumulativeInflation *= (1 + yearlyInflation);
          
          // Apply returns to each asset using normal distribution
          Object.keys(assetValues).forEach(assetName => {
            const baseReturn = assetReturns[assetName];
            // Use normal distribution with Box-Muller transform
            // Real return = nominal return - inflation
            const nominalReturn = generateNormalRandom(baseReturn, volatility.value);
            const realReturn = nominalReturn - yearlyInflation;
            assetValues[assetName] *= (1 + realReturn);
            
            // Add contributions for this asset (already in real terms from input)
            const contribution = liquidAssets.value.find(a => a.name === assetName)?.contribution_rate || 0;
            assetValues[assetName] += contribution;
          });
          
          // Calculate new total
          currentTotal = Object.values(assetValues).reduce((sum, val) => sum + val, 0);
          
          // Apply net savings (income - expenses) - already in real terms from yearData
          const netSavings = yearData.savings || 0;
          
          // If net savings is negative (withdrawals needed)
          if (netSavings < 0) {
            const withdrawalNeeded = Math.abs(netSavings);
            
            if (currentTotal < withdrawalNeeded) {
              // Portfolio depleted - this is a failure
              hasFailure = true;
              currentTotal = 0;
              path.push(0);
              // Continue to show the failure state
              for (let i = yearIndex + 1; i < years.length; i++) {
                path.push(0);
              }
              break;
            } else {
              // Withdraw proportionally from each asset
              const withdrawalRatio = withdrawalNeeded / currentTotal;
              Object.keys(assetValues).forEach(assetName => {
                assetValues[assetName] *= (1 - withdrawalRatio);
              });
              currentTotal -= withdrawalNeeded;
            }
          } else {
            // Positive savings - add to portfolio based on target allocation
            Object.keys(assetValues).forEach(assetName => {
              const targetWeight = targetAllocation[assetName] || 0;
              assetValues[assetName] += netSavings * targetWeight;
            });
            currentTotal += netSavings;
          }
          
          // Annual rebalancing to target allocation
          if (currentTotal > 0 && yearIndex % 1 === 0) { // Rebalance annually
            const rebalancedValues = {};
            Object.keys(targetAllocation).forEach(assetName => {
              rebalancedValues[assetName] = currentTotal * targetAllocation[assetName];
            });
            assetValues = rebalancedValues;
          }
          
          // Check for failure (portfolio below zero)
          if (currentTotal < 0) {
            hasFailure = true;
            currentTotal = 0;
            path.push(0);
            // Fill rest of path with zeros
            for (let i = yearIndex + 1; i < years.length; i++) {
              path.push(0);
            }
            break;
          }
          
          path.push(currentTotal);
        }
        
        if (hasFailure) {
          failures++;
        }
        
        simResults.push(path);
      }
      
      // Calculate percentiles
      const percentiles = calculatePercentiles(simResults);
      
      // Calculate success rate (portfolio > threshold at end AND never depleted)
      const finalValues = simResults.map(path => path[path.length - 1]);
      const successes = finalValues.filter(v => v > successThreshold.value).length;
      successRate.value = Math.round((successes / numSimulations.value) * 100);
      
      simulationData.value = {
        years,
        percentiles,
        median: percentiles.p50,
        simResults: simResults.slice(0, 100), // Keep first 100 for display
        failureRate: Math.round((failures / numSimulations.value) * 100)
      };
      
      isSimulating.value = false;
    };

    // Calculate percentiles from simulation results
    const calculatePercentiles = (simResults) => {
      const numYears = simResults[0].length;
      const percentiles = {
        p5: [],
        p25: [],
        p50: [],
        p75: [],
        p95: []
      };
      
      for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        const valuesAtYear = simResults.map(path => path[yearIndex]).sort((a, b) => a - b);
        
        percentiles.p5.push(valuesAtYear[Math.floor(valuesAtYear.length * 0.05)]);
        percentiles.p25.push(valuesAtYear[Math.floor(valuesAtYear.length * 0.25)]);
        percentiles.p50.push(valuesAtYear[Math.floor(valuesAtYear.length * 0.50)]);
        percentiles.p75.push(valuesAtYear[Math.floor(valuesAtYear.length * 0.75)]);
        percentiles.p95.push(valuesAtYear[Math.floor(valuesAtYear.length * 0.95)]);
      }
      
      return percentiles;
    };

    // Highcharts configuration
    const highchartsOptions = computed(() => {
      if (!simulationData.value) return {};
      
      const { years, percentiles } = simulationData.value;
      
      return {
        chart: {
          type: 'area',
          height: 500,
          backgroundColor: '#ffffff'
        },
        title: { 
          text: 'Portfolio Value Projections',
          style: { fontSize: '14px', color: '#666' }
        },
        xAxis: {
          categories: years,
          title: { text: 'Year' },
          tickInterval: Math.ceil(years.length / 10)
        },
        yAxis: {
          title: { text: 'Portfolio Value ($)' },
          labels: {
            formatter: function() {
              return props.formatCurrency(this.value);
            }
          },
          min: 0
        },
        tooltip: {
          shared: true,
          formatter: function() {
            let tooltip = `<b>Year ${this.x}</b><br/>`;
            
            // Show percentiles in order
            const percentileLabels = {
              '95th Percentile': 0,
              '75th Percentile': 1,
              'Median': 2,
              '25th Percentile': 3,
              '5th Percentile': 4
            };
            
            this.points.sort((a, b) => percentileLabels[a.series.name] - percentileLabels[b.series.name]);
            
            this.points.forEach(point => {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${props.formatCurrency(point.y)}</b><br/>`;
            });
            
            return tooltip;
          }
        },
        legend: {
          enabled: true,
          align: 'right',
          verticalAlign: 'top',
          layout: 'vertical',
          x: 0,
          y: 50
        },
        plotOptions: {
          arearange: {
            lineWidth: 0,
            marker: { enabled: false },
            states: {
              hover: { enabled: false }
            }
          },
          area: {
            lineWidth: 0,
            marker: { enabled: false },
            fillOpacity: 0.3,
            threshold: null
          },
          line: {
            lineWidth: 2,
            marker: { enabled: false }
          }
        },
        series: [
          {
            name: '5-95% Confidence',
            data: percentiles.p5.map((val, i) => [val, percentiles.p95[i]]),
            type: 'arearange',
            color: 'rgba(200, 212, 240, 0.3)',
            fillColor: 'rgba(200, 212, 240, 0.3)',
            lineWidth: 0,
            zIndex: 1,
            showInLegend: true,
            enableMouseTracking: false
          },
          {
            name: '25-75% Confidence',
            data: percentiles.p25.map((val, i) => [val, percentiles.p75[i]]),
            type: 'arearange',
            color: 'rgba(145, 167, 217, 0.5)',
            fillColor: 'rgba(145, 167, 217, 0.5)',
            lineWidth: 0,
            zIndex: 2,
            showInLegend: true,
            enableMouseTracking: false
          },
          {
            name: 'Median',
            data: percentiles.p50,
            type: 'line',
            color: '#3b5ca8',
            lineWidth: 3,
            zIndex: 5,
            showInLegend: true,
            marker: { enabled: false }
          },
          {
            name: '95th Percentile',
            data: percentiles.p95,
            type: 'line',
            color: 'rgba(200, 212, 240, 0.8)',
            lineWidth: 1,
            dashStyle: 'Dash',
            zIndex: 3,
            showInLegend: false,
            enableMouseTracking: true
          },
          {
            name: '75th Percentile',
            data: percentiles.p75,
            type: 'line',
            color: 'rgba(145, 167, 217, 0.8)',
            lineWidth: 1,
            dashStyle: 'Dash',
            zIndex: 3,
            showInLegend: false,
            enableMouseTracking: true
          },
          {
            name: '25th Percentile',
            data: percentiles.p25,
            type: 'line',
            color: 'rgba(145, 167, 217, 0.8)',
            lineWidth: 1,
            dashStyle: 'Dash',
            zIndex: 3,
            showInLegend: false,
            enableMouseTracking: true
          },
          {
            name: '5th Percentile',
            data: percentiles.p5,
            type: 'line',
            color: 'rgba(200, 212, 240, 0.8)',
            lineWidth: 1,
            dashStyle: 'Dash',
            zIndex: 3,
            showInLegend: false,
            enableMouseTracking: true
          }
        ]
      };
    });

    // Run simulation on mount
    onMounted(() => {
      runSimulation();
    });

    return {
      numSimulations,
      volatility,
      successThreshold,
      inflationOption,
      inflationRate,
      inflationVolatility,
      isSimulating,
      simulationData,
      successRate,
      highchartsOptions,
      runSimulation
    };
  }
};
</script>
